import React, { useEffect, useState } from "react";
import { signalsApi } from "../../lib/api";
import { Card } from "../shared";
import { Mail, Send, Plus, X, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export const DigestPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => { signalsApi.digest().then(setCfg).catch(() => setCfg(null)); }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      setCfg(await signalsApi.saveDigest({
        enabled: next.enabled, recipients: next.recipients || [], industry: next.industry || "",
        geography: next.geography || "", thesis: next.thesis || "", includeSavedScans: !!next.includeSavedScans,
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the digest settings"); }
  };

  const addEmail = () => {
    const e = email.trim();
    if (!e.includes("@")) return toast.error("That does not look like an email address");
    setEmail("");
    return save({ recipients: [...(cfg.recipients || []), e] });
  };

  const sendNow = async () => {
    setSending(true);
    const tid = toast.loading("Running every watched market — this takes a few minutes…");
    try {
      const res = await signalsApi.sendDigest();
      setPreview(res.preview || "");
      setCfg(await signalsApi.digest());
      toast.success(`${res.sent ? "Digest sent" : "Digest built but the send failed"} · ${res.new} new signals · ${res.players} new names`, { id: tid });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the digest", { id: tid }); }
    setSending(false);
  };

  if (!cfg) return null;

  return (
    <>
      <Card title="Weekly Market Digest" testid="digest-card" right={(
        <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 11.5, letterSpacing: "-0.01em", textTransform: "none" }}>
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="digest-enabled" />
          {cfg.enabled ? "On" : "Off"}
        </label>
      )}>
        <div className="dg-when" data-testid="digest-schedule"><CalendarClock size={13} /> {cfg.schedule} — every watched market is re-scanned and you get what is new plus the current top 10.</div>

        <div className="intel-grid" style={{ marginTop: 12 }}>
          <div className="field"><label>Watched market — industry</label>
            <input className="inp inp-text" value={cfg.industry || ""} onChange={(e) => setCfg({ ...cfg, industry: e.target.value })}
              onBlur={() => save({})} placeholder="e.g. commercial grounds maintenance" data-testid="digest-industry" /></div>
          <div className="field"><label>Geography</label>
            <input className="inp inp-text" value={cfg.geography || ""} onChange={(e) => setCfg({ ...cfg, geography: e.target.value })}
              onBlur={() => save({})} placeholder="Texas" data-testid="digest-geo" /></div>
          <div className="field" style={{ gridColumn: "span 2" }}><label>Thesis / angle to track</label>
            <input className="inp inp-text" value={cfg.thesis || ""} onChange={(e) => setCfg({ ...cfg, thesis: e.target.value })}
              onBlur={() => save({})} placeholder="e.g. regional density, recurring commercial contracts" data-testid="digest-thesis" /></div>
        </div>

        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5, marginTop: 10 }}>
          <input type="checkbox" checked={!!cfg.includeSavedScans} onChange={(e) => save({ includeSavedScans: e.target.checked })} data-testid="digest-saved-scans" />
          Also re-run my saved scans
        </label>

        <div className="dg-block">
          <div className="dg-label">Recipients</div>
          <div className="dg-chips" data-testid="digest-recipients">
            {(cfg.willSendTo || []).map((e) => {
              const manual = (cfg.recipients || []).includes(e);
              return (
                <span className="dg-chip" key={e} data-testid={`digest-chip-${e}`}>
                  <Mail size={10} />{e}{manual ? null : <i>admin</i>}
                  {manual ? (
                    <button onClick={() => save({ recipients: (cfg.recipients || []).filter((x) => x !== e) })} data-testid={`digest-remove-${e}`}><X size={10} /></button>
                  ) : null}
                </span>
              );
            })}
          </div>
          <div className="dg-add">
            <input className="inp inp-text" style={{ width: 260 }} value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEmail()} placeholder="add another recipient" data-testid="digest-email-input" />
            <button className="btn btn-sm" onClick={addEmail} data-testid="digest-add-email"><Plus size={12} style={{ marginRight: 4 }} />Add</button>
          </div>
        </div>

        <div className="dg-block">
          <div className="dg-label">Markets in the next digest ({(cfg.targets || []).length})</div>
          <div className="dg-chips" data-testid="digest-targets">
            {(cfg.targets || []).length
              ? cfg.targets.map((t) => <span className="dg-chip" key={t}>{t}</span>)
              : <span className="ir-fine">Nothing to scan yet — set a watched market above or save a scan on the Market signals tab.</span>}
          </div>
        </div>

        <div className="sig-actions">
          <button className="btn btn-primary" onClick={sendNow} disabled={sending} data-testid="digest-send-now">
            <Send size={13} style={{ marginRight: 6 }} />{sending ? "Building…" : "Send me a digest now"}
          </button>
          {cfg.lastSentAt ? <span className="ir-fine" data-testid="digest-last">Last sent {String(cfg.lastSentAt).slice(0, 16).replace("T", " ")} · {cfg.lastResult}</span> : <span className="ir-fine">Not sent yet</span>}
        </div>
      </Card>

      {(preview || cfg.lastBody) ? (
        <Card title="What the email looks like" style={{ marginTop: 12 }} testid="digest-preview">
          <pre className="dg-preview">{preview || cfg.lastBody}</pre>
        </Card>
      ) : null}
    </>
  );
};
